import { getCachedOrListConnectors } from "@mirage/service-connectors";
import useConnectors from "@mirage/service-connectors/useConnectors";
import { isConnectorVisible } from "@mirage/service-connectors/utils";
import { useDismissableModule } from "@mirage/service-onboarding/useDismissableModule";
import { useConnectors as useConnectorsAtom } from "@mirage/shared/atoms/connectors";
import type { ConnectorName } from "@mirage/shared/connectors";
import { usePopulateAllAvailableConnectorsAtomData } from "@mirage/shared/connectors/usePopulateAllAvailableConnectorsAtomData";
import { useDashUserAttributes } from "@mirage/shared/eligibility/hooks/useDashUserAttributes";
import { isDashStandaloneWeb } from "@mirage/shared/utils/environment-detection";

const DISMISSED_ADD_APPS_UPSELL_ON_SERP = "dismissedAddAppsUpsellOnSerp";
const CONNECTION_COUNT_THRESHOLD = 3;

const APP_UPSSELL_ORDER: ConnectorName[] = [
  "gmail",
  "googleCalendar",
  "outlook",
  "zoom",
  "miro",
];

export const useAddAppsUpsellOnSerp = () => {
  const { userAttributes } = useDashUserAttributes();
  // For future version, there will be some conditions added to the dismiss suggestion based on the user's behavior.
  // For now, we don't have manual dismiss yet and dismiss after 7 days.
  const { dismissed, setDismissed } = useDismissableModule(
    DISMISSED_ADD_APPS_UPSELL_ON_SERP,
  );

  const { connections, hasFinishedInitialFetch } = useConnectors();
  const numConnections = connections.length;

  const allConnectors = useConnectorsAtom();
  // filter out connectors that has connections already
  const availableConnectors = allConnectors.data.filter(
    (c) =>
      !connections.find(
        (c2) => c2.connector?.id_attrs?.type === c.id_attrs?.type,
      ),
  );

  const upsellConnectors = availableConnectors
    .filter((c) =>
      APP_UPSSELL_ORDER.includes(c.id_attrs?.type as ConnectorName),
    )
    .sort((a, b) => {
      const indexA = APP_UPSSELL_ORDER.indexOf(
        a.id_attrs?.type as ConnectorName,
      );
      const indexB = APP_UPSSELL_ORDER.indexOf(
        b.id_attrs?.type as ConnectorName,
      );
      return indexA - indexB;
    });

  usePopulateAllAvailableConnectorsAtomData({
    getCachedOrListConnectors,
    isConnectorVisible,
  });

  // only show add apps upsell on serp for web bridge users for now
  const isWebBridgeUser =
    isDashStandaloneWeb() && !!userAttributes?.hasDashInFss;

  const showUpsell =
    isWebBridgeUser &&
    !dismissed &&
    hasFinishedInitialFetch &&
    numConnections < CONNECTION_COUNT_THRESHOLD;

  return {
    showUpsell,
    setDismissed: () => setDismissed(true),
    upsellConnectors,
  };
};
